import { Selector } from '@ngxs/store';
import { BusinessUser, BusinessUserRole } from '@models/businesses';
import { BusinessUsersState } from '@state/business-users/business-users.state';
import { LlcState } from '@state/llc/llc.state';
import { INTEGRATION_TYPES, IntegrationCardConfig, INTEGRATIONS } from '.';
import { BusinessState } from '@state/business';
import { TitlePlusState } from '@state/global/title-insurance/title-plus/title-plus.state';
import { UserProfileState } from '@state/user-profile';
import { isSet } from '@core/util/value';
import { of } from 'rxjs';
import { UrlService } from '@services/url/url.service';
import { CLOSER_SYNC_DESCRIPTION } from '@services/closer-sync/closer-sync.service';
import { SIGN_IT_DESCRIPTION } from './sign-it';

export class IntegrationsSelectors {
	@Selector([
		BusinessUsersState.isSyncIntegrationConnected,
		BusinessUsersState.getLawyers,
		BusinessUsersState.getSyncEnabledLawyers,
		BusinessUsersState.getLoggedInBusinessUser,
	])
	private static syncIntergration(
		syncConnected: boolean,
		lawyers: BusinessUser[],
		syncEnabledLawyers: BusinessUser[],
		user: BusinessUser,
	): IntegrationCardConfig {
		return {
			imageSource: '/assets/images/integrations/sync-icon.png',
			title: 'Sync By Closer',
			description: CLOSER_SYNC_DESCRIPTION,
			href: UrlService.Profile_Integrations_CloserSync(),
			connected$: of(syncConnected),
			lawyersConnected: syncEnabledLawyers?.length ?? 0,
			connectButtonText:
				user?.role === BusinessUserRole.CLERK && 'Invite Lawyer',
			totalLawyers: lawyers?.length ?? 0,
		};
	}

	@Selector([IntegrationsSelectors.syncIntergration, LlcState.connected])
	static [INTEGRATION_TYPES.ElectronicMortgageInstructions](
		syncIntegration: IntegrationCardConfig,
		llcConnected: boolean,
	): Map<string, IntegrationCardConfig> {
		return new Map([
			[INTEGRATIONS.CLOSER_SYNC, syncIntegration],
			[
				INTEGRATIONS.LLC,
				{
					imageSource: '/assets/images/integrations/llc-logo.png',
					title: 'Lender Lawyer Connect',
					description:
						'Simplify and digitize the instructing processing, reporting and document-handling of your TD, BNS and MMS residential mortgages at no cost.',
					href: UrlService.Profile_Integrations_LLC(),
					connected$: of(llcConnected),
				},
			],
		]);
	}

	@Selector()
	static [INTEGRATION_TYPES.PracticeManagement](): Map<
		string,
		IntegrationCardConfig
	> {
		return new Map([
			[
				INTEGRATIONS.LEAP,
				{
					connected$: of(null),
					connectButtonText: 'View',
					imageSource: '/assets/images/leap/leap-logo-left-flush.png',
					description:
						'Access Closer directly within LEAP to complete end-to-end conveyancing matters without ever leaving the LEAP platform.',
					href: UrlService.Profile_Integrations_Leap(),
				},
			],
		]);
	}

	@Selector([
		LlcState.connected,
		BusinessState.chicagoFirmName,
		TitlePlusState.connected,
	])
	static [INTEGRATION_TYPES.TitleInsurance](
		llcConnected: boolean,
		chicagoLawfirm: string,
		titlePlusConnected: boolean,
	): Map<string, IntegrationCardConfig> {
		return new Map([
			[
				INTEGRATIONS.FCT,
				{
					imageSource: '/assets/images/integrations/fct-logo.svg',
					title: 'First Canadian Title',
					description:
						'Order title insurance from this industry leading provider without leaving Closer.',
					href: UrlService.Profile_Integrations_FCT(),
					connected$: of(llcConnected),
				},
			],
			[
				INTEGRATIONS.CHICAGO_TITLE,
				{
					imageSource: '/assets/images/integrations/chicago-title-logo.svg',
					title: 'Chicago Title',
					description:
						'Order title insurance from this industry leading provider without leaving Closer.',
					href: UrlService.Profile_Integrations_Chicago(),
					connected$: of(isSet(chicagoLawfirm)),
				},
			],
			[
				INTEGRATIONS.STEWART_TITLE,
				{
					imageSource: '/assets/images/integrations/stewart-title-logo.svg',
					title: 'Stewart Title',
					description:
						'Order title insurance from this industry leading provider without leaving Closer.',
					href: UrlService.Profile_Integrations_Stewart(),
					connected$: of(undefined),
				},
			],
			[
				INTEGRATIONS.TITLE_PLUS,
				{
					imageSource: '/assets/images/integrations/title-plus-logo.svg',
					title: 'TitlePLUS',
					description:
						'Order title insurance from this industry leading provider without leaving Closer.',
					href: UrlService.Profile_Integrations_TitlePLUS(),
					connected$: of(titlePlusConnected),
				},
			],
		]);
	}

	@Selector([UserProfileState.getTeranetUsername])
	static [INTEGRATION_TYPES.PropertyAndWritsSearching](
		teranetUsername: string,
	): Map<string, IntegrationCardConfig> {
		return new Map([
			[
				INTEGRATIONS.TERANET,
				{
					imageSource: '/assets/images/integrations/teranet-logo.svg',
					title: 'Teranet',
					description:
						'Access the Electronic Land Registration and Writs databases from within Closer to	complete property and writs searches.',
					href: UrlService.Profile_Integrations_Teranet(),
					connected$: of(isSet(teranetUsername)),
				},
			],
		]);
	}
	@Selector([BusinessState.getTreefortConnected])
	static [INTEGRATION_TYPES.IdentityVerification](
		treefortConnected: boolean,
	): Map<string, IntegrationCardConfig> {
		return new Map([
			[
				INTEGRATIONS.TREEFORT,
				{
					imageSource: '/assets/images/integrations/treefort-logo.svg',
					title: 'Treefort',
					description:
						"Access Treefort's digital identity & fraud prevention technology within Closer for seamless and secure client verification.",
					href: UrlService.Profile_Integrations_Treefort(),
					connected$: of(treefortConnected),
				},
			],
		]);
	}
	@Selector([BusinessState.isSignITSelected])
	static [INTEGRATION_TYPES.ElectronicSigning](
		isSignItSelected: boolean,
	): Map<string, IntegrationCardConfig> {
		return new Map([
			[
				INTEGRATIONS.SIGN_IT,
				{
					connected$: of(isSignItSelected),
					imageSource: '/assets/images/integrations/sign-it/sign-it-logo.svg',
					title: 'SignIT',
					description: SIGN_IT_DESCRIPTION,
					href: UrlService.Profile_Integrations_SignIt(),
				},
			],
		]);
	}
}
